import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

type TabMessage =
  | { type: 'FORCE_RELEASE'; targetTabId: string; requesterTabId: string }
  | { type: 'OWNER_HEARTBEAT'; ownerTabId: string; timestamp: number };

@Injectable({
  providedIn: 'root'
})
export class TabService {
  private readonly lockName = 'voicebot-active-tab-lock';
  private readonly ownerStorageKey = 'voicebot-active-tab-owner';
  private readonly ownerHeartbeatMs = 2000;
  private readonly ownerStaleMs = 10000;

  private readonly isDuplicateSubject = new BehaviorSubject<boolean>(false);
  readonly isDuplicate$: Observable<boolean> = this.isDuplicateSubject.asObservable();

  private readonly tabId = this.getOrCreateTabId();
  private readonly supportsNavigatorLocks = typeof navigator !== 'undefined' && 'locks' in navigator;
  private readonly supportsBroadcastChannel = typeof BroadcastChannel !== 'undefined';

  private readonly windowRef: Window | null = globalThis.window ?? null;

  private channel: BroadcastChannel | null = null;
  private releaseLockResolver: (() => void) | null = null;
  private ownerHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private initialized = false;
  private ownsLock = false;
  private initialCheckDone = false;
  private resolveInitialCheck: (() => void) | null = null;
  private initialCheckPromise: Promise<void> = Promise.resolve();

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.initialCheckPromise = new Promise<void>((resolve) => {
      this.resolveInitialCheck = resolve;
    });
    this.setupChannel();
    this.setupLifecycleHandlers();

    if (this.supportsNavigatorLocks) {
      this.acquireNavigatorLock();
      return;
    }

    this.acquireFallbackLock();
  }

  async awaitInitialCheck(): Promise<void> {
    this.initialize();
    await this.initialCheckPromise;
  }

  async takeover(): Promise<boolean> {
    this.initialize();

    const currentOwner = this.readOwnerRecord();
    if (currentOwner && currentOwner.tabId !== this.tabId) {
      this.postMessage({
        type: 'FORCE_RELEASE',
        targetTabId: currentOwner.tabId,
        requesterTabId: this.tabId
      });
    }

    this.releaseFallbackOwnershipForCurrentTab();
    this.releaseNavigatorLock();

    if (this.supportsNavigatorLocks) {
      await this.retryAcquireNavigatorLock();
    } else {
      this.acquireFallbackLock(true);
    }

    return this.ownsLock && !this.isDuplicateSubject.value;
  }

  private setupChannel(): void {
    if (!this.supportsBroadcastChannel) {
      return;
    }

    this.channel = new BroadcastChannel(this.lockName);
    this.channel.onmessage = (event: MessageEvent<TabMessage>) => {
      this.handleMessage(event.data);
    };
  }

  private setupLifecycleHandlers(): void {
    if (!this.windowRef) {
      return;
    }
    this.windowRef.addEventListener('storage', this.handleStorageEvent);
    this.windowRef.addEventListener('beforeunload', this.cleanup);
    this.windowRef.addEventListener('pagehide', this.cleanup);
  }

  private acquireNavigatorLock(): void {
    navigator.locks.request(this.lockName, { ifAvailable: true, mode: 'exclusive' }, async (lock) => {
      if (!lock) {
        this.ownsLock = false;
        this.setDuplicateState(true);
        this.markInitialCheckDone();
        return;
      }

      this.ownsLock = true;
      this.setDuplicateState(false);
      this.setOwnerRecord(this.tabId);
      this.startOwnerHeartbeat();
      this.markInitialCheckDone();

      await new Promise<void>((resolve) => {
        this.releaseLockResolver = resolve;
      });

      this.releaseLockResolver = null;
      this.ownsLock = false;
      this.stopOwnerHeartbeat();
      this.clearOwnerRecordIfOwnedByCurrentTab();
    }).catch(() => {
      this.ownsLock = false;
      this.setDuplicateState(true);
      this.markInitialCheckDone();
    });
  }

  private async retryAcquireNavigatorLock(): Promise<void> {
    let attempt = 0;
    const maxAttempts = 8;

    while (attempt < maxAttempts && !this.ownsLock) {
      this.acquireNavigatorLock();
      await this.delay(150);
      if (this.ownsLock) {
        return;
      }
      attempt += 1;
    }

    this.setDuplicateState(!this.ownsLock);
  }

  private acquireFallbackLock(force = false): void {
    const owner = this.readOwnerRecord();
    const now = Date.now();
    const ownerIsStale = !owner || now - owner.timestamp > this.ownerStaleMs;
    const canOwn = force || !owner || owner.tabId === this.tabId || ownerIsStale;

    if (!canOwn) {
      this.ownsLock = false;
      this.setDuplicateState(true);
      this.markInitialCheckDone();
      return;
    }

    this.ownsLock = true;
    this.setDuplicateState(false);
    this.setOwnerRecord(this.tabId);
    this.startOwnerHeartbeat();
    this.markInitialCheckDone();
  }

  private startOwnerHeartbeat(): void {
    this.stopOwnerHeartbeat();

    this.ownerHeartbeatTimer = globalThis.setInterval(() => {
      if (!this.ownsLock) {
        return;
      }

      this.setOwnerRecord(this.tabId);
      this.postMessage({
        type: 'OWNER_HEARTBEAT',
        ownerTabId: this.tabId,
        timestamp: Date.now()
      });
    }, this.ownerHeartbeatMs);
  }

  private stopOwnerHeartbeat(): void {
    if (this.ownerHeartbeatTimer !== null) {
      globalThis.clearInterval(this.ownerHeartbeatTimer);
      this.ownerHeartbeatTimer = null;
    }
  }

  private handleMessage(message: TabMessage): void {
    if (message.type === 'FORCE_RELEASE' && message.targetTabId === this.tabId && this.ownsLock) {
      this.releaseNavigatorLock();
      this.releaseFallbackOwnershipForCurrentTab();
      this.setDuplicateState(true);
      return;
    }

    if (message.type === 'OWNER_HEARTBEAT' && message.ownerTabId !== this.tabId && !this.ownsLock) {
      this.setDuplicateState(true);
    }
  }

  private readonly handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== this.ownerStorageKey) {
      return;
    }

    const owner = this.readOwnerRecord();
    if (!owner) {
      this.setDuplicateState(false);
      return;
    }

    this.setDuplicateState(owner.tabId !== this.tabId && !this.ownsLock);
  };

  private releaseNavigatorLock(): void {
    if (this.releaseLockResolver) {
      this.releaseLockResolver();
      this.releaseLockResolver = null;
    }
  }

  private releaseFallbackOwnershipForCurrentTab(): void {
    this.stopOwnerHeartbeat();
    this.clearOwnerRecordIfOwnedByCurrentTab();
    this.ownsLock = false;
  }

  private readonly cleanup = (): void => {
    this.releaseNavigatorLock();
    this.releaseFallbackOwnershipForCurrentTab();
    if (this.windowRef) {
      this.windowRef.removeEventListener('storage', this.handleStorageEvent);
      this.windowRef.removeEventListener('beforeunload', this.cleanup);
      this.windowRef.removeEventListener('pagehide', this.cleanup);
    }
    this.channel?.close();
  };

  private postMessage(message: TabMessage): void {
    this.channel?.postMessage(message);
  }

  private setDuplicateState(isDuplicate: boolean): void {
    if (this.isDuplicateSubject.value !== isDuplicate) {
      this.isDuplicateSubject.next(isDuplicate);
    }
  }

  isDuplicateSnapshot(): boolean {
    return this.isDuplicateSubject.value;
  }

  private getOrCreateTabId(): string {
    const existingId = this.safeSessionGet('voicebot-tab-id');
    if (existingId) {
      return existingId;
    }

    const id = globalThis.crypto?.randomUUID?.() ?? `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    this.safeSessionSet('voicebot-tab-id', id);
    return id;
  }

  private setOwnerRecord(tabId: string): void {
    this.safeLocalSet(
      this.ownerStorageKey,
      JSON.stringify({
        tabId,
        timestamp: Date.now()
      })
    );
  }

  private readOwnerRecord(): { tabId: string; timestamp: number } | null {
    const raw = this.safeLocalGet(this.ownerStorageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as { tabId?: string; timestamp?: number };
      if (!parsed.tabId || typeof parsed.timestamp !== 'number') {
        return null;
      }
      return { tabId: parsed.tabId, timestamp: parsed.timestamp };
    } catch {
      return null;
    }
  }

  private clearOwnerRecordIfOwnedByCurrentTab(): void {
    const owner = this.readOwnerRecord();
    if (owner?.tabId === this.tabId) {
      this.safeLocalRemove(this.ownerStorageKey);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      globalThis.setTimeout(resolve, ms);
    });
  }

  private safeSessionGet(key: string): string | null {
    try {
      return globalThis.sessionStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  private safeSessionSet(key: string, value: string): void {
    try {
      globalThis.sessionStorage?.setItem(key, value);
    } catch {
      // ignore write failures in restricted contexts
    }
  }

  private safeLocalGet(key: string): string | null {
    try {
      return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  private safeLocalSet(key: string, value: string): void {
    try {
      globalThis.localStorage?.setItem(key, value);
    } catch {
      // ignore write failures in restricted contexts
    }
  }

  private safeLocalRemove(key: string): void {
    try {
      globalThis.localStorage?.removeItem(key);
    } catch {
      // ignore remove failures in restricted contexts
    }
  }

  private markInitialCheckDone(): void {
    if (this.initialCheckDone) {
      return;
    }

    this.initialCheckDone = true;
    this.resolveInitialCheck?.();
    this.resolveInitialCheck = null;
  }
}
